import { toast, type Toast } from "react-hot-toast";
import {
  CheckCircle,
  XCircle,
  Info,
  TriangleAlert,
  LoaderCircle
} from "lucide-react";

interface ToastOptions {
  message: string;
  type?: "success" | "error" | "info" | "warning" | "loading";
  duration?: number;
  icon?: React.ReactNode;
  backgroundColor?: string;
  color?: string;
}

export const showToast = ({
  message,
  type = "info",
  duration = 3000,
  icon,
  backgroundColor,
  color = "#fff",
}: ToastOptions) => {
  
  const defaultColors: Record<string, string> = {
    success: "#10b981",
    error: "#ef4444",
    info: "#3b82f6",
    warning: "#f59e0b",
    loading: "#6366f1",
  };

  // Default icons based on type
  const defaultIcons: Record<string, React.ReactNode> = {
    success: <CheckCircle size={20} />,
    error: <XCircle size={20} />,
    info: <Info size={20} />,
    warning: <TriangleAlert size={20} />,
    loading: <LoaderCircle size={20} className="animate-spin" />,
  };

  const bgColor = backgroundColor || defaultColors[type];
  const iconToShow = icon || defaultIcons[type];

  toast.custom(
    (t: Toast) => (
      <div
        style={{
          backgroundColor: bgColor,
          color: color,
          padding: "16px 20px",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
          maxWidth: "400px",
          minWidth: "280px",
          opacity: t.visible ? 1 : 0,
          transform: t.visible ? "translateX(0)" : "translateX(100px)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          flexShrink: 0 
        }}>
          {iconToShow}
        </div>
        <div style={{ 
          flex: 1, 
          fontSize: "15px", 
          fontWeight: "500", 
          wordBreak: "break-word",
          lineHeight: "1.5"
        }}>
          {message}
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          style={{
            background: "transparent",
            border: "none",
            color: color,
            cursor: "pointer",
            fontSize: "24px",
            padding: "0 4px",
            opacity: 0.8,
            lineHeight: 1,
            flexShrink: 0,
            fontWeight: "300",
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
        >
          ×
        </button>
      </div>
    ),
    { 
      duration,
      position: "top-right",
    }
  );
};

// Convenience methods
export const toastSuccess = (message: string, options?: Partial<ToastOptions>) => {
  showToast({ message, type: "success", ...options });
};

export const toastError = (message: string, options?: Partial<ToastOptions>) => {
  showToast({ message, type: "error", ...options });
};

export const toastInfo = (message: string, options?: Partial<ToastOptions>) => {
  showToast({ message, type: "info", ...options });
};

export const toastWarning = (message: string, options?: Partial<ToastOptions>) => { 
  showToast({ message, type: "warning", ...options });
};

export const toastLoading = (message: string, options?: Partial<ToastOptions>) => {
  showToast({ message, type: "loading", ...options });
};